// Voice ordering from the composer on a restaurant menu page. The server reads the words against the menu
// (POST /support/v1/order-intents) and only proposes; the screens that own the basket run the actions:
// - the menu page registers addToBasket (LOW risk: runs at once);
// - the basket registers checkout (MEDIUM risk: runs only after the customer taps confirm, On Account only).
// Without both handlers (another page, the basket not mounted) the composer sends a support message as before.

import { allergenGateApplies } from './basketCheck';

const handlers = {};
const listeners = new Set();

export const MENU_SCREEN = 'menu.restaurant';
// The open basket: on a pilot restaurant, words there still update the order and the allergy answer.
export const CART_SCREEN = 'checkout.cart';

/**
 * @param {'addToBasket'|'checkout'|'summary'} name
 * @param {Function} handler
 * @returns {Function} unregister: only removes this handler, not a newer one
 */
export function registerVoiceOrderHandler(name, handler) {
  handlers[name] = handler;
  notify();
  return () => {
    if (handlers[name] === handler) {
      delete handlers[name];
      notify();
    }
  };
}

export function getVoiceOrderHandler(name) {
  return handlers[name] || null;
}

// Voice ordering applies to this context: a menu page with a restaurant (or, on an allergen-pilot restaurant, its
// open basket), and the basket can take the items.
export function voiceOrderingFor(context) {
  return (
    !!context &&
    (context.screenId === MENU_SCREEN ||
      (context.screenId === CART_SCREEN && !!context.ids && allergenGateApplies(context.ids.restaurantId))) &&
    !!context.ids &&
    context.ids.restaurantId !== undefined &&
    context.ids.restaurantId !== null &&
    !!handlers.addToBasket &&
    !!handlers.checkout
  );
}

// The basket total and whether it can be placed On Account, for the confirm card.
export function subscribeVoiceOrder(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function notifyVoiceOrder() {
  notify();
}

function notify() {
  setTimeout(() => listeners.forEach(listener => listener()), 0);
}

export function formatMoney(amount, currencySymbol) {
  const value = Number(amount);
  return `${currencySymbol || '€'}${(Number.isFinite(value) ? value : 0).toFixed(2)}`;
}

// "1 × Caesar Club, €11.50" or "2 × Caesar Club, €23.00 and 1 × Soup, €4.00".
export function readBack(items, currencySymbol) {
  const lines = (items || []).map(
    item =>
      `${item.qty} × ${item.name}, ${formatMoney(
        (item.unitPrice || 0) * (item.qty || 1),
        currencySymbol
      )}`
  );
  if (lines.length <= 1) return lines.join('');
  return `${lines.slice(0, -1).join('; ')} and ${lines[lines.length - 1]}`;
}

// Items the server proposed, from its ADD_TO_BASKET actions (LOW risk) matched to its items.
export function itemsToAdd(intent) {
  if (!intent || intent.decision !== 'ORDER') return [];
  const items = intent.items || [];
  return (intent.actions || [])
    .filter(action => action.type === 'ADD_TO_BASKET' && action.risk === 'LOW')
    .map(action => {
      const item = items.find(candidate => candidate.productId === action.productId);
      return item ? { ...item, qty: action.qty || item.qty || 1 } : null;
    })
    .filter(Boolean);
}

// The server proposed checkout On Account, behind an explicit confirm.
export function offersCheckout(intent) {
  return (
    !!intent &&
    (intent.actions || []).some(
      action =>
        action.type === 'CHECKOUT' &&
        action.risk === 'MEDIUM' &&
        action.paymentMethod === 'ON_ACCOUNT' &&
        action.requiresConfirm === true
    )
  );
}



// WEBPACK FOOTER //
// ./src/support/voiceOrder.js